/**
 * Error reporting for the admin panel.
 *
 * Replaces Sentry. There is no Cloud Logging client for browsers, and GCP
 * credentials must never reach a bundle, so reports are POSTed to the backend,
 * which re-emits them into Cloud Logging under its own service account. See
 * backend/src/routes/clientLogs.ts.
 *
 * Every function here is best-effort and silent. Telemetry that surfaces its
 * own failures to an operator is worse than no telemetry.
 */

const INGEST_URL = `${import.meta.env.VITE_API_URL ?? '/api'}/client-logs`

/** Set at build time by Vite; identifies which bundle a stack came from. */
const RELEASE = import.meta.env.VITE_BUILD_ID ?? 'dev'

type Level = 'error' | 'fatal' | 'warning'

interface Breadcrumb {
  at: string
  category?: string
  message?: string
}

/**
 * A short rolling trail of what happened before the error. Capped, because it
 * is sent on every report and the endpoint truncates past 20 anyway.
 */
const MAX_BREADCRUMBS = 20
let breadcrumbs: Breadcrumb[] = []

export function addBreadcrumb(category: string, message: string): void {
  // Rebuilt rather than mutated, so a report being serialised concurrently
  // cannot observe a half-updated trail.
  breadcrumbs = [...breadcrumbs, { at: new Date().toISOString(), category, message }]
    .slice(-MAX_BREADCRUMBS)
}

/**
 * De-duplicate within a session.
 *
 * A render loop throwing the same error can fire hundreds of times a second;
 * without this the panel would DoS its own ingest endpoint and trip the
 * per-IP limiter, losing the reports that matter.
 */
const seen = new Map<string, number>()
const DEDUPE_WINDOW_MS = 60_000

function shouldSend(key: string): boolean {
  const now = Date.now()
  const last = seen.get(key)
  if (last && now - last < DEDUPE_WINDOW_MS) return false
  seen.set(key, now)
  if (seen.size > 100) seen.clear()
  return true
}

interface ReportOptions {
  level?: Level
  context?: Record<string, unknown>
}

/** Report an error. Never throws, never blocks, never surfaces a failure. */
export function captureException(error: unknown, options: ReportOptions = {}): void {
  try {
    const err = error instanceof Error ? error : new Error(String(error))
    const message = err.message || 'Unknown error'
    if (!shouldSend(`${message}::${err.stack?.slice(0, 200) ?? ''}`)) return

    const body = JSON.stringify({
      app: 'admin',
      release: RELEASE,
      platform: navigator.userAgent.slice(0, 40),
      level: options.level ?? 'error',
      message,
      stack: err.stack,
      context: {
        ...(options.context ?? {}),
        url: window.location.pathname + window.location.search,
      },
      breadcrumbs,
      occurredAt: new Date().toISOString(),
    })

    // keepalive so a report sent while the tab is closing still goes out —
    // which is exactly when a fatal error tends to happen.
    void fetch(INGEST_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Offline, blocked, or the API is the thing that is down. Drop it.
    })
  } catch {
    // Reporting an error must never become an error.
  }
}

/**
 * Catch what never reaches a React error boundary: rejected promises and
 * errors thrown outside the render tree. Between them and ErrorBoundary, the
 * panel has no silent failure mode left.
 */
export function initTelemetry(): void {
  window.addEventListener('unhandledrejection', (event) => {
    captureException(event.reason, { context: { kind: 'unhandledrejection' } })
  })

  window.addEventListener('error', (event) => {
    // Resource load failures (a missing image) fire here too with no `error`;
    // they are not exceptions and would only add noise.
    if (!event.error) return
    captureException(event.error, { context: { kind: 'window.onerror' } })
  })
}
